import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import * as ExcelJS from 'exceljs';
import { DatePipe } from '@angular/common';

type AOA = Array<Array<any>>;

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

@Injectable()
export class ExcelService {
  msgPrev: string;
  FilePath: string;
  data: any[][];

  constructor(private datePipe: DatePipe) { }

  // General method for saving files
  private saveFile(buffer: any, fileName: string, extension = EXCEL_EXTENSION): void {
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    FileSaver.saveAs(data, `${fileName}_export_${new Date().getTime()}${extension}`);
  }

  // Export as CSV
  public exportAsExcelFile(json: any[], excelFileName: string): void {
    const worksheet = XLSX.utils.json_to_sheet(json);
    const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(workbook, { bookType: 'csv', type: 'array' });
    this.saveFile(excelBuffer, excelFileName, '.csv');
  }

  // Export as XLSX
  public exportAsXlsFile(json: any[], excelFileName: string): void {
    const worksheet = XLSX.utils.json_to_sheet(json);
    const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveFile(excelBuffer, excelFileName);
  }

  // Export multiple sheets as XLSX
  public exportAsXlsSheets(json: any[], excelFileName: string): void {
    const workbook = XLSX.utils.book_new();

    json.forEach(value => {
      const worksheet = XLSX.utils.json_to_sheet(value.Value);
      XLSX.utils.book_append_sheet(workbook, worksheet, value.Key);
    });

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveFile(excelBuffer, excelFileName);
  }

  // Convert an array to CSV format
  ConvertToCSV(objArray: any): string {
    const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
    let str = '';
    let row = "";

    for (const index in objArray[0]) {
      row += index + ',';
    }
    row = row.slice(0, -1);
    str += row + '\r\n';

    array.forEach(item => {
      let line = '';
      for (const index in item) {
        if (line !== '') line += ',';
        line += item[index];
      }
      str += line + '\r\n';
    });
    return str;
  }

  // File upload handling
  public FileUpload(evt: any, model: any[]): void {
    const target: DataTransfer = <DataTransfer>(evt.target);
    if (target.files.length !== 1) {
      this.msgPrev = 'Erreur : Veuillez sélectionner un seul fichier.';
      return;
    }

    const file = target.files[0];
    this.FilePath = file.name;
    const reader = new FileReader();

    reader.onload = (e: any) => {
      try {
        const wb: XLSX.WorkBook = XLSX.read(e.target.result, { type: 'binary' });
        const ws: XLSX.WorkSheet = wb.Sheets[wb.SheetNames[0]];
        this.data = XLSX.utils.sheet_to_json(ws, { header: 1 }) as AOA;
        this.msgPrev = wb.SheetNames[0];
      } catch {
        this.msgPrev = 'Erreur : Fichier invalide';
      }
    };

    reader.onloadend = () => {
      evt.target.value = null;
    };

    reader.readAsBinaryString(file);
  }

  // Generate Excel file with user-specific details
  public generateSpecificExcelUsers(data: any, fileName: string): void {
    const title = 'Date';
    const header = ["User Access", "Affiliate", "User Admin", "Enable", "Brand", "Customer", "Web App", "Dashboards", "Access created", "Access created by"];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Users');

    worksheet.columns = [
      { key: 'UserAccess', width: 50 },
      { key: 'Affiliate', width: 16 },
      { key: 'UserAdmin', width: 12 },
      { key: 'Enable', width: 12 },
      { key: 'Brand', width: 32 },
      { key: 'Customer', width: 32 },
      { key: 'WebApp', width: 32 },
      { key: 'Dashboards', width: 32 },
      { key: 'AccessCreated', width: 24 },
      { key: 'AccessCreatedBy', width: 50 }
    ];

    worksheet.properties.defaultRowHeight = 32;
    let nameCol = worksheet.getColumn('A');
    nameCol.width = 50;

    let titleRow = worksheet.addRow([title]);
    let cellh = worksheet.getCell('A1');
    cellh.fill = { type: 'pattern', pattern: 'darkGrid', fgColor: { argb: 'FFFFFFFF' }, bgColor: { argb: 'FF0070C0' } };
    cellh.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    titleRow.font = { name: 'Calibri', size: 12 };
    worksheet.addRow([this.datePipe.transform(new Date(), "dd MMM yyyy")]);
    worksheet.addRow([]);

    let headerRow = worksheet.addRow(header);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'darkGrid', fgColor: { argb: 'FFFFFFFF' }, bgColor: { argb: 'FF0070C0' } };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    data.forEach(d => {
      const row = [d.UserAccess, d.Affiliate, d.UserAdmin, d.Enable, d.Brand, d.Customer, d.WebApp, d.Dashboards, d.AccessCreated, d.AccessCreatedBy];
      worksheet.addRow(row);
    });

    workbook.xlsx.writeBuffer().then(buffer => {
      const blob = new Blob([buffer], { type: EXCEL_TYPE });
      FileSaver.saveAs(blob, `${fileName}_export_${new Date().getTime()}${EXCEL_EXTENSION}`);
    });
  }
}
